import { useTranslation } from '@frontend/i18n';
import { SpinningLoader, Text, TextLink } from '@frontend/design-system';
import { DEFAULT_SIGNATURE_FONT, getSignatureFontFamily, isSignatureFont } from '../../signature/constants';
import { ESignValue, isImageESign } from '../../signature/esign-value';
import { useSignatureFonts } from '../../signature/fonts';
import { reportSignatureError } from '../../signature/monitoring';
import { useMediaImageUrl } from '../../signature/use-media-blob';
import {
  previewImageStyle,
  previewStatusStyle,
  previewTextStyle,
  thumbnailImageStyle,
  thumbnailTextStyle,
} from './esign-field.styles';

interface ESignPreviewProps {
  value: ESignValue;
  fieldKey: string;
  /** Thumbnail size for the history list. */
  compact?: boolean;
}

/** Renders a committed eSign value: the uploaded image (wet/image) or the typed text in its font. */
export const ESignPreview = ({ value, fieldKey, compact }: ESignPreviewProps) =>
  isImageESign(value) ? (
    <ESignImagePreview mediaId={value.data} fieldKey={fieldKey} compact={compact} />
  ) : (
    <ESignTextPreview text={value.data} font={value.font_type} compact={compact} />
  );

const ESignImagePreview = ({
  mediaId,
  fieldKey,
  compact,
}: {
  mediaId: string;
  fieldKey: string;
  compact?: boolean;
}) => {
  const { t } = useTranslation('digital-forms-experience');
  const { url, isLoading, isError, refetch } = useMediaImageUrl(mediaId, {
    onError: (error) => reportSignatureError(error, { feature: 'esign', operation: 'download', fieldKey }),
  });

  if (isLoading) {
    return (
      <span css={previewStatusStyle}>
        <SpinningLoader size='xs' />
        {!compact && (
          <Text as='span' size='small' color='light'>
            {t('Loading signature...')}
          </Text>
        )}
      </span>
    );
  }

  if (isError || !url) {
    if (compact)
      return (
        <Text as='span' size='small' color='error'>
          {t('Unavailable')}
        </Text>
      );
    return (
      <span css={previewStatusStyle}>
        <Text as='span' size='small' color='error'>
          {t('Failed to load signature')}
        </Text>
        <TextLink
          size='small'
          onClick={(event: React.MouseEvent) => {
            event.stopPropagation();
            refetch();
          }}
        >
          {t('Retry')}
        </TextLink>
      </span>
    );
  }

  return <img src={url} alt={t('Your signature')} css={compact ? thumbnailImageStyle : previewImageStyle} />;
};

const ESignTextPreview = ({ text, font, compact }: { text: string; font: string; compact?: boolean }) => {
  const signatureFont = isSignatureFont(font) ? font : DEFAULT_SIGNATURE_FONT;
  useSignatureFonts([signatureFont]);

  return (
    <Text
      as='span'
      css={[compact ? thumbnailTextStyle : previewTextStyle, { fontFamily: getSignatureFontFamily(signatureFont) }]}
    >
      {text}
    </Text>
  );
};
